import request from './request'

/**
 * 创建商品
 * @param {Object} params 创建商品请求参数
 * @param {string} params.title 商品标题
 * @param {string} [params.description] 商品描述
 * @param {string} [params.coverImage] 封面图URL
 * @param {string[]} [params.detailImages] 详情图片列表
 * @param {number} params.fullCoursePrice 整套课程价格（分）
 * @param {number} [params.salePrice] 促销价（分）
 * @param {Array} params.chapters 章节列表
 * @param {number} params.chapters[].chapterSeq 章节序号
 * @param {string} params.chapters[].chapterTitle 章节标题
 * @param {string} [params.chapters[].chapterDesc] 章节描述
 * @param {number} params.chapters[].specPrice 章节价格（分）
 * @param {boolean} [params.chapters[].isFree] 是否免费
 * @param {string} [params.chapters[].videoUrl] 视频URL（COS完整访问路径）
 * @returns {Promise} 返回商品ID
 */
export function createGoods(params) {
  if (!params || typeof params !== 'object') {
    return Promise.reject(new Error('请提供有效的商品参数'))
  }
  if (!params.title || typeof params.title !== 'string') {
    return Promise.reject(new Error('商品标题不能为空'))
  }
  if (typeof params.fullCoursePrice !== 'number' || params.fullCoursePrice < 0) {
    return Promise.reject(new Error('整套课程价格必须是非负数'))
  }
  if (!Array.isArray(params.chapters) || params.chapters.length === 0) {
    return Promise.reject(new Error('至少需要添加一个章节'))
  }
  
  return request.post('/api/admin/goods', params)
}

/**
 * 获取商品详情
 * @param {number|string} id 商品ID
 * @returns {Promise} 返回商品详情
 */
export function getGoodsDetail(id) {
  if (!id && id !== 0) {
    return Promise.reject(new Error('商品ID不能为空'))
  }
  
  return request.get(`/api/admin/goods/${id}`)
}

/**
 * 更新商品
 * @param {number|string} id 商品ID
 * @param {Object} params 更新商品请求参数
 * @param {string} [params.title] 商品标题
 * @param {string} [params.description] 商品描述
 * @param {string} [params.coverImage] 封面图URL
 * @param {string[]} [params.detailImages] 详情图片列表
 * @param {number} [params.fullCoursePrice] 整套课程价格（分）
 * @param {number} [params.salePrice] 促销价（分）
 * @param {Array} [params.chapters] 章节列表
 * @param {number} params.chapters[].chapterSeq 章节序号
 * @param {string} params.chapters[].chapterTitle 章节标题
 * @param {string} [params.chapters[].chapterDesc] 章节描述
 * @param {number} params.chapters[].specPrice 章节价格（分）
 * @param {boolean} [params.chapters[].isFree] 是否免费
 * @param {string} [params.chapters[].videoUrl] 视频URL（COS完整访问路径）
 * @returns {Promise}
 */
export function updateGoods(id, params) {
  if (!id && id !== 0) {
    return Promise.reject(new Error('商品ID不能为空'))
  }
  if (!params || typeof params !== 'object') {
    return Promise.reject(new Error('请提供有效的更新参数'))
  }
  
  return request.put(`/api/admin/goods/${id}`, params)
}

/**
 * 删除商品
 * @param {number|string} id 商品ID
 * @returns {Promise}
 */
export function deleteGoods(id) {
  if (!id && id !== 0) {
    return Promise.reject(new Error('商品ID不能为空'))
  }
  
  return request.delete(`/api/admin/goods/${id}`)
}

/**
 * 更新商品状态
 * @param {number|string} id 商品ID
 * @param {number} status 状态：0-草稿 1-上架 2-下架
 * @returns {Promise}
 */
export function updateGoodsStatus(id, status) {
  if (!id && id !== 0) {
    return Promise.reject(new Error('商品ID不能为空'))
  }
  if (typeof status !== 'number' || ![0, 1, 2].includes(status)) {
    return Promise.reject(new Error('状态值必须是 0（草稿）、1（上架）或 2（下架）'))
  }
  
  return request.put(`/api/admin/goods/${id}/status`, null, {
    params: {
      status: status
    }
  })
}

/**
 * 获取商品列表
 * @param {Object} [params] 查询参数
 * @param {number} [params.pageNum] 页码
 * @param {number} [params.pageSize] 每页数量
 * @param {string} [params.startTime] 开始时间
 * @param {string} [params.endTime] 结束时间
 * @param {Array} [params.sortItemList] 排序项列表
 * @param {boolean} [params.sortItemList[].isAsc] 是否升序
 * @param {string} [params.sortItemList[].column] 排序列
 * @param {string} [params.title] 商品标题（模糊查询）
 * @param {string} [params.goodsCode] 商品编码
 * @param {number} [params.goodsType] 商品类型：1-课程
 * @param {number} [params.status] 状态：0-草稿 1-上架 2-下架
 * @param {number} [params.size] 每页数量
 * @param {number} [params.current] 当前页码
 * @returns {Promise} 返回分页商品列表
 */
export function getGoodsList(params = {}) {
  return request.get('/api/admin/goods/list', {
    params
  })
}

/**
 * 切换推荐状态
 * @param {number|string} id 商品ID
 * @returns {Promise}
 */
export function toggleRecommend(id) {
  if (!id && id !== 0) {
    return Promise.reject(new Error('商品ID不能为空'))
  }
  
  return request.put(`/api/admin/goods/${id}/recommend`)
}

